// @ts-check
import { useAuth0 } from '@auth0/auth0-react';
import { css, jsx } from '@emotion/core';
import { Icon, Tag, Tooltip } from 'antd';
import Link from 'next/link';
import Router from 'next/router';
import React from 'react';
import { useSelector } from 'react-redux';

import Button from '@/src/shared/components/Button';
import btnStyle from '@/src/shared/components/Button/style';
import { colors } from '@/styles';

const style = {
  card: css`
    padding: 16px 12px;
    background-color: white;
    box-shadow: 0 6px 12px 0 rgba(35, 32, 32, 0.1);
    display: flex;
    flex-direction: column;
  `,
  tagItems: css`
    height: 24px;
    margin-block-end: 8px;
  `,
  tagItem: css`
    border-radius: 25px;
    background: ${colors.accentGrayUltralight};
    font-size: 1.2rem;
    font-weight: 800;
    padding: 4px;
    margin-right: 5px;
    height: 5px;
  `,
  title: css`
    font-weight: 900;
    margin-bottom: 1.6rem;
    font-size: 1.8rem;
    color: ${colors.secondaryBlueDark};
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  date: css`
    font-size: 1.2rem;
    padding-bottom: 25px;
  `,
  link: css`
    font-size: 1.4rem;
    display: flex;
    gap: 0.8rem;
    margin-left: auto;
    justify-self: flex-end;
    margin-top: auto;
  `,
};

/**
 * @param {{
 *   title: string;
 *   date: string;
 *   buttonText: string;
 *   buttonUrl: string;
 *   tags?: string[];
 *   memberAccessLevels: {WebAccessName: string}[] | null;
 * }} props
 */
export default function WhitepaperAttachmentCard(props) {
  const { title, date, buttonText, buttonUrl, tags, memberAccessLevels } = props;
  const { role: roles, isLoggedIn } = useSelector(/** @param {{ auth: {role: string[], isLoggedIn: boolean } }} state */(state) => state.auth);
  const { loginWithRedirect } = useAuth0();

  let showTooltip = tags.length > 2;
  let tooltipTitle = showTooltip ? tags.slice(2).join(', ') : null;

  const hasMemberAccessLevels = memberAccessLevels?.length > 0;
  const hasAccessToContent = (memberAccessLevels ?? []).some(level => roles.includes(level?.WebAccessName))
  const linkProp = isLoggedIn ? { target: '_blank' } : {};

  return (
    <div css={style.card}>
      <div css={style.tagItems}>
        {showTooltip && (
          <Tooltip title={tooltipTitle}>
            <Tag>+ {tags.length - 2} more</Tag>
          </Tooltip>
        )}
        {tags.slice(0, 2).map(tag => (
          <Tag key={tag}>{tag}</Tag>
        ))}
      </div>
      {title && (
        <h3 css={style.title}>{title}</h3>
      )}
      {date && <div css={style.date}>Published on {date}</div>}
      
      {/* Download button */}
      {hasMemberAccessLevels && !isLoggedIn && (
        <Button
          theme="default"
          css={style.link}
          size="small"
          onClick={() =>
            loginWithRedirect({
              appState: {
                returnTo: Router.pathname,
              },
            })
          }
          {...linkProp}
        >
          {buttonText}
          <Icon type="lock" />
        </Button>
      )}
      {(!hasMemberAccessLevels || (hasMemberAccessLevels && isLoggedIn && hasAccessToContent)) && (
        <Button
          theme="default"
          size="small"
          css={style.link}
          href={buttonUrl}
          link
          {...linkProp}
        >
          {buttonText}
          <Icon type="download" />
        </Button>
      )}
      {isLoggedIn && hasMemberAccessLevels && !hasAccessToContent && (
        <Tooltip title="Restricted Access. This content is not included in your company's APCIA subscription.">
          <Button 
            theme="disabled"
            css={style.link}
            size="small"
          >
            {buttonText}
            <Icon type="lock" />
          </Button>
        </Tooltip>
      )}
    </div>
  );
};
